import { Component, inject, OnInit } from '@angular/core';
import { NgbCalendar, NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { FlagAnalysis, GetItemsParams, Patient } from '../service/reports.model';
import { SharedService } from 'src/app/services/shared.service';
import { ReportsService } from '../service/reports.service';
import { AppointmentFilter, CaseType, ToastType } from 'src/app/enums/enums';
import { ApiService } from 'src/app/services/api.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-flag',
  templateUrl: './flag.component.html',
  styleUrls: ['./flag.component.scss']
})
export class FlagComponent implements OnInit {
  calendar = inject(NgbCalendar);
  private Unsubscribe = new Subject<void>();
  public PaginationParams!: GetItemsParams;
  public IsLoading = false;
  public TotaReports = 0;
  public reportList: any[] = [];
  public SelectedAppt: string = '1';
  /* Input Dates */
  fromDate: NgbDate | null = this.calendar.getNext(this.calendar.getToday(), 'd', -10);
  toDate: NgbDate | null = this.calendar.getToday();
  MaxDate: NgbDateStruct = { year: new Date().getFullYear(), month: new Date().getMonth() + 1, day: new Date().getDate() };

  fromStr = `${this.fromDate?.year}-${this.fromDate?.month}-${this.fromDate?.day}`;
  toStr = `${this.toDate?.year}-${this.toDate?.month}-${this.toDate?.day}`;
  currentClinic = 0;

  showAll = false;
  showMD = false;
  showMRI = false;
  showNeuro = false;
  showOrtho = false;
  showPain = false;

  AppointmentTypes = Object.keys(AppointmentFilter)
    .filter((key) => !isNaN(Number(key)))
    .map((key) => ({
      key: Number(key),
      value: AppointmentFilter[Number(key)]
    }));

  constructor(public sharedService: SharedService, private reportsService: ReportsService, public apiService: ApiService, private toast: ToastService) {
    this.PaginationParams = {
      sortColumn: 'firstName',
      clinicId: this.sharedService.CurrentClinic.value || 0,
      sortOrder: 'asc',
      fromDate: this.fromStr,
      toDate: this.toStr,
      appointmentType: this.SelectedAppt
    };
  }
  ngOnInit(): void {
    this.currentClinic = this.sharedService.CurrentClinic.value || 0;
    this.sharedService.CurrentClinic.pipe(takeUntil(this.Unsubscribe)).subscribe((val) => {
      this.PaginationParams.clinicId = Number(val);
      this.currentClinic = Number(val);
      this.GetFlagAnalysisReport(this.PaginationParams);
      this.setColumnVisibility(this.SelectedAppt);
    });
  }

  GetFlagAnalysisReport(params: GetItemsParams) {
    this.IsLoading = true;
    this.reportsService.getReportsFA<FlagAnalysis>(params, 'FlagAnalysis').subscribe({
      next: (res) => {
        this.reportList = [...res.result];
        this.reportList.map((flagAnalysis: FlagAnalysis) => {
          flagAnalysis.clinicName = this.apiService.GetClinicName(flagAnalysis.clinicId);
        });
        this.TotaReports = this.reportList.length;
      },
      error: (err) => {
        this.IsLoading = false;
      },
      complete: () => (this.IsLoading = false)
    });
  }

  toDateChange(date: string) {
    this.toStr = date;
    this.PaginationParams.toDate = date;
    this.GetFlagAnalysisReport(this.PaginationParams);
  }

  fromDateChange(date: string) {
    this.PaginationParams.fromDate = date;
    this.fromStr = date;
  }

  exportReport() {
    this.reportsService.exportReportFA(this.PaginationParams, 'FlagAnalysis').subscribe({
      next: (res) => {
        this.toast.showToast({ message: res.message, type: ToastType.Success });
      },
      error: (err) => {
        this.toast.showToast({ message: err.message, type: ToastType.Error });
      },
      complete: () => {}
    });
  }

  SortData(sortBy: string) {
    this.PaginationParams.sortColumn = sortBy;
    this.PaginationParams.sortOrder = this.PaginationParams.sortOrder === 'asc' ? 'desc' : 'asc';
    this.GetFlagAnalysisReport(this.PaginationParams);
  }

  getCaseName(id: number): string {
    return CaseType[id] || '';
  }

  // Method to set column visibility based on condition
  setColumnVisibility(condition: string): void {
    const appointmentType = this.AppointmentTypes.find((item) => item.key === Number(condition))?.value || '';

    // Reset all flags
    this.showAll = false;
    this.showMD = false;
    this.showMRI = false;
    this.showNeuro = false;
    this.showOrtho = false;
    this.showPain = false;

    // Set flags based on condition
    switch (appointmentType.toLowerCase()) {
      case 'all':
        this.showAll = true;
        this.showMD = true;
        this.showMRI = true;
        this.showNeuro = true;
        this.showOrtho = true;
        this.showPain = true;
        break;
      case 'md':
        this.showMD = true;
        break;
      case 'mri':
        this.showMRI = true;
        break;
      case 'neuro':
        this.showNeuro = true;
        break;
      case 'ortho':
        this.showOrtho = true;
        break;
      case 'pain':
        this.showPain = true;
        break;
      default:
        // Default case, e.g., show nothing or a specific group
        this.showMD = true; // Example
    }
  }

  filterAppointmentType(e: Event) {
    const selectedVal = (e.target as HTMLSelectElement).value;
    this.setColumnVisibility(selectedVal);
    this.PaginationParams.appointmentType = selectedVal;
    this.GetFlagAnalysisReport(this.PaginationParams);
    return this.AppointmentTypes.find((item) => item.key === Number(selectedVal))?.value || '';
  }

  ngOnDestroy(): void {
    this.Unsubscribe.next();
    this.Unsubscribe.complete();
  }
}
